import { CheckCircleIcon, ClockIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { atom, useAtom, useSetAtom } from 'jotai';
import React from 'react';
import { ICrmEmailMessage, isCrmError, IUnsentEmail, useCrmEmailCompose } from '../../../services/crm';

export interface QueuedSend {
    message: ICrmEmailMessage;
    /** Who it's to, for the toast. */
    label: string;
    /** Where the content goes back to on undo (usually: reopen the composer with it). */
    onUndo?: (unsent: IUnsentEmail) => void;
    /** Called once the email has actually gone (after the undo window). */
    onSent?: () => void;
}

const queueAtom = atom<(QueuedSend & { key: string; queuedAt: number; phase: 'waiting' | 'sent' | 'too_late' })[]>([]);

// #region useSendQueue
/** Hands a just-queued email to the undo toast. */
export const useSendQueue = () => {
    const set = useSetAtom(queueAtom);
    return React.useCallback((item: QueuedSend) => {
        set((q) => [...q.filter((x) => x.message.id !== item.message.id), { ...item, key: `${item.message.id}-${Date.now()}`, queuedAt: Date.now(), phase: 'waiting' }]);
    }, [set]);
};

const whenLabel = (iso: string): string =>
    new Date(iso).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// #region SendQueueToasts
/**
 * Bottom-left: "Sending to Indy in 8s · Undo · Send now", or "Scheduled for Tue 9:00 AM · Undo".
 * Undo takes the email back before it leaves and returns its content to where it came from.
 */
export const SendQueueToasts: React.FunctionComponent<{}> = () => {
    const [queue, setQueue] = useAtom(queueAtom);
    const [api] = useCrmEmailCompose();
    const [now, setNow] = React.useState(Date.now());

    React.useEffect(() => {
        if (!queue.length) return undefined;
        const t = window.setInterval(() => setNow(Date.now()), 250);
        return () => window.clearInterval(t);
    }, [queue.length]);

    // Waiting sends whose window has passed become "Sent"; finished toasts fade after a moment.
    React.useEffect(() => {
        for (const item of queue) {
            const due = Date.parse(item.message.send_at ?? '') || 0;
            const scheduled = due - item.queuedAt > 60_000;
            if (item.phase === 'waiting' && !scheduled && now >= due + 1500) {
                setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, phase: 'sent' } : x)));
                item.onSent?.();
                window.setTimeout(() => setQueue((q) => q.filter((x) => x.key !== item.key)), 2500);
            }
            if (item.phase === 'waiting' && scheduled && now >= item.queuedAt + 8000) {
                setQueue((q) => q.filter((x) => x.key !== item.key));
            }
        }
    }, [now, queue, setQueue]);

    if (!queue.length) return null;

    const undo = async (item: (typeof queue)[number]) => {
        const r = await api.undo(item.message.id);
        if (isCrmError(r)) {
            setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, phase: 'too_late' } : x)));
            window.setTimeout(() => setQueue((q) => q.filter((x) => x.key !== item.key)), 3000);
            return;
        }
        setQueue((q) => q.filter((x) => x.key !== item.key));
        item.onUndo?.(r);
    };

    const sendNow = async (item: (typeof queue)[number]) => {
        const r = await api.sendNow(item.message.id);
        if (!isCrmError(r)) setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, message: { ...x.message, send_at: new Date().toISOString() } } : x)));
    };

    return (
        <div className="fixed bottom-4 left-4 z-[65] flex max-w-[calc(100vw-2rem)] flex-col gap-2" role="status" aria-live="polite">
            {queue.map((item) => {
                const due = Date.parse(item.message.send_at ?? '') || now;
                const scheduled = due - item.queuedAt > 60_000;
                const secs = Math.max(0, Math.ceil((due - now) / 1000));
                return (
                    <div key={item.key} className="flex items-center gap-3 rounded-xl bg-zinc-900 py-2 pl-3 pr-2 text-sm text-white shadow-xl ring-1 ring-white/10">
                        {item.phase === 'sent' ? <CheckCircleIcon className="h-5 w-5 text-emerald-400" />
                            : scheduled ? <ClockIcon className="h-5 w-5 text-sky-300" /> : <PaperAirplaneIcon className="h-5 w-5 text-zinc-300" />}
                        <span className="min-w-0 truncate">
                            {item.phase === 'sent' ? <>Sent to <b>{item.label}</b></>
                                : item.phase === 'too_late' ? 'Too late to undo — it has gone'
                                    : scheduled ? <>Scheduled to <b>{item.label}</b> for {whenLabel(item.message.send_at as string)}</>
                                        : <>Sending to <b>{item.label}</b> in {secs}s</>}
                        </span>
                        {item.phase === 'waiting' ? (
                            <span className="flex shrink-0 items-center gap-1">
                                <button type="button" onClick={() => undo(item)} className="rounded-lg px-2 py-1 font-semibold text-amber-300 hover:bg-white/10">Undo</button>
                                {!scheduled ? <button type="button" onClick={() => sendNow(item)} className="rounded-lg px-2 py-1 text-zinc-300 hover:bg-white/10">Send now</button> : null}
                            </span>
                        ) : null}
                    </div>
                );
            })}
        </div>
    );
};
